// @flow
import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import ComingSoonBanner from 'components/organisms/comingSoonBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import RenderSEO from 'components/atoms/renderSEO';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const StoresPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    footerLink,
    getFooterLinkData,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    bannerData,
  } = props;

  const { isMobile } = useViewport();
  const metaTagSEO = {
    metaTitle: 'Stores | Total Environment',
    metaDescription:
      'With an innate drive to create enhanced living experience for people, Total Environment designs apartments, penthouses and villas that people actually want to live in. From live music to handcrafted furniture to fine craft beer, we leave our mark of excellence everywhere. To know more about our homes and handcrafted experiences, call + 91 99000 78000.',
    metaKeywords: '',
  };

  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getFooterLinkData();
    getBannerData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (!contact || !siteSettings || !verticals || !bannerData || !footerLink) {
    return null;
  }

  if (bannerData.metaTitle) metaTagSEO.metaTitle = bannerData.metaTitle;
  if (bannerData.metaDescription)
    metaTagSEO.metaDescription = bannerData.metaDescription;
  if (bannerData.metaKeywords)
    metaTagSEO.metaKeywords = bannerData.metaKeywords;

  const componentList = [
    ...(!isEmpty(bannerData)
      ? [
          {
            component: ComingSoonBanner,
            data: { bannerData },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Store',
                breadcrumbLink: RouteNames.store,
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Store',
          breadcrumbLink: RouteNames.store,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Store',
          breadcrumbLink: RouteNames.store,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.getContactsLoading(state) ||
      HomeSelector.getVerticalsLoading(state) ||
      HomeSelector.getBannerLoading(state) ||
      FooterSelector.isLoading(state) ||
      HomeSelector.getSiteSettingsLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    footerLink: FooterSelector.getFooterData(state),
    bannerData: HomeSelector.getBanners(state, true, 'store', ''),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getBannerData,
      getFooterLinkData,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(StoresPage);
